import { useState } from "react";
import { toast } from "sonner";
import { FileText, X } from "lucide-react";
import { api, absUrl } from "./api";

const ACTION_LABEL = { quote: "Request a quote", call: "Call us", enquiry: "Contact us" };
const FIELD_LABEL = { logo: "Logo", photo: "Photo", evidence: "Supporting document" };
const LOGO_LABEL = { supplied: "Supplied", toFollow: "To follow", none: "None" };

const FIELD_NAME = { "business.mainLocation": "Main location", contactReason: "What customers usually contact you for" };
const colourPref = (cp) => {
  if (!cp) return "Not asked (older intake)";
  if (cp.choice !== "suggested") return "No preference — Alias to choose";
  return (
    <span className="inline-flex items-center gap-2" data-testid="intake-colour-pref">
      <span className="inline-block h-4 w-4 rounded-full ring-1 ring-white/30" style={{ background: cp.hex }} />
      {cp.name} <span className="text-slate-500 text-xs">{cp.hex}</span>
    </span>
  );
};
const lines = (arr) => (arr && arr.length ? arr.join("\n") : null);

function rowsFor(i) {
  const b = i.business || {};
  return [
    ["Your business", [
      ["Business name", b.name], ["Type of business", b.type], ["Main location", b.mainLocation],
      ["Areas served", lines(b.serviceAreas)], ["What the business does", b.description],
    ]],
    ["Services & customers", [
      ["Services", lines((i.services || []).map((s) => (s.note ? `${s.name} — ${s.note}` : s.name)))],
      ["What customers usually contact you for", i.contactReason],
      ["Why customers choose you", lines(i.differentiators)],
      ["What visitors should do first", ACTION_LABEL[i.preferredAction] || i.preferredAction],
    ]],
    ["Contact & branding", [
      ["Phone", b.phone], ["Email", b.email], ["Opening hours", b.hours],
      ["Business address", b.address], ["Existing website / domain", b.domain],
      ["Colour preference", colourPref(i.brand?.colourPreference)],
      ["Logo", LOGO_LABEL[(i.brand?.logo || {}).status] || (i.brand?.logo || {}).status],
    ]],
    ["Trust & final details", [
      ["Trust claims (unverified)", lines((i.trustClaims || []).map((t) => `${t.type}: ${t.detail}`))],
      ["Customer reviews", lines((i.testimonials || []).map((t) => `“${t.text}” — ${t.name}${t.area ? `, ${t.area}` : ""}`))],
      ["Anything else", i.notes],
    ]],
  ];
}

export function IntakeAnswersButton({ jobId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try { setData(await api.intake(jobId)); } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={open} disabled={busy} data-testid="view-intake-answers"
        className="w-full flex items-center justify-center gap-2 rounded-2xl bg-slate-900/70 ring-1 ring-slate-700 py-3 font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60 transition-colors">
        <FileText className="h-4 w-4" /> {busy ? "Loading…" : "View intake answers"}
      </button>
      {data && <IntakePanel data={data} onClose={() => setData(null)} />}
    </>
  );
}

function IntakePanel({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm overflow-y-auto" data-testid="intake-answers-panel">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Client intake answers <span className="text-xs text-slate-500 font-normal">(read-only)</span></h2>
          <button onClick={onClose} data-testid="close-intake-answers" className="rounded-lg p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          {(data.corrections || []).map((c, i) => (
            <p key={i} data-testid="intake-correction-note" className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-2 text-xs text-amber-200">
              {c.fields.map((f) => FIELD_NAME[f] || f).join(", ")} added by Alias on {new Date(c.ts).toLocaleString()}. {c.source}
            </p>
          ))}
          {rowsFor(data.intake).map(([title, rows]) => (
            <section key={title} className="rounded-2xl bg-slate-900 ring-1 ring-slate-800 p-4">
              <p className="text-[11px] uppercase tracking-wide text-amber-400/80 mb-3">{title}</p>
              <dl className="space-y-3">
                {rows.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-slate-400">{k}</dt>
                    <dd className="text-sm whitespace-pre-wrap break-words" data-testid={`intake-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v || <span className="text-slate-600">—</span>}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
          <section className="rounded-2xl bg-slate-900 ring-1 ring-slate-800 p-4">
            <p className="text-[11px] uppercase tracking-wide text-amber-400/80 mb-3">Uploaded files</p>
            {data.files.length === 0 ? <p className="text-sm text-slate-500" data-testid="intake-no-files">No files uploaded.</p> : (
              <ul className="space-y-2" data-testid="intake-files">
                {data.files.map((f) => (
                  <li key={f.url} className="text-sm">
                    <span className="text-slate-400">{FIELD_LABEL[f.field] || f.field}: </span>
                    <a href={absUrl(f.url)} target="_blank" rel="noopener noreferrer" className="text-amber-300 underline break-all">{f.name}</a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
